import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Save, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import io from "socket.io-client";
const base_url = process.env.REACT_APP_API_URL;

const iconData = [
  { name: "umbrella", icon: "☔" },
  { name: "soccer", icon: "⚽" },
  { name: "sun", icon: "☀️" },
  { name: "lamp", icon: "🪔" },
  { name: "cow", icon: "🐄" },
  { name: "bucket", icon: "🪣" },
  { name: "kite", icon: "🪁" },
  { name: "lattu", icon: "🪀" },
  { name: "rose", icon: "🌹" },
  { name: "butterfly", icon: "🦋" },
  { name: "dove", icon: "🕊️" },
  { name: "rabbit", icon: "🐰" },
];

const getIconByName = (name) => {
  const item = iconData.find((d) => d.name === name);
  return item ? item.icon : "❓";
};

const getSelectedDisplay = (result) => {
  if (!result) {
    return "Select Icon";
  }
  const item = iconData.find((d) => d.name === result);
  if (item) {
    return (
      <span className="d-flex align-items-center text-start">
        <span className="me-2 fs-5">{item.icon}</span>
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: "150px" }}
        >
          {item.name}
        </span>
      </span>
    );
  }
  return result;
};

const AdminManageTitli = () => {
  const token = localStorage.getItem("admin_token");
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    !(window.innerWidth < 768),
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentGame, setCurrentGame] = useState(null);
  const [gameBets, setGameBets] = useState([]);
  const [gameHistory, setGameHistory] = useState([]);
  const [totalBets, setTotalBets] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [timmer, setTimmer] = useState(120);
  const [selectedResult, setSelectedResult] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpen1, setIsOpen1] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isOpen3, setIsOpen3] = useState(false);
  const [auth] = useState(localStorage.getItem("admin_token"));
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameBetsDetails, setGameBetsDetails] = useState([]);
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const [totalHistoryPages, setTotalHistoryPages] = useState(1);
  const socketRef = useRef(null);
  const HISTORY_LIMIT = 10;

  // Socket setup
  useEffect(() => {
    if (!token) return;

    socketRef.current = io(base_url, {
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socketRef.current.on("connect", () => {
      // console.log("Admin socket connected");
      socketRef.current.emit("joinRoom", "admin");
    });

    // Listen for new game
    socketRef.current.on("newGame", (data) => {
      console.log("New game from cron:", data);
      fetchCurrentGame();
      fetchHistory();
    });

    // Listen for new bet (updated to recalculate properly)
    socketRef.current.on("newBetTitli", (newBet) => {
      console.log("New bet placed:", newBet);
      setGameBets((prev) => {
        const updated = [...prev, newBet];
        // Recalculate totals from updated array
        const totalBetCount = updated.length;
        const totalBetAmount = updated.reduce(
          (sum, bet) => sum + parseFloat(bet.betAmount || 0),
          0,
        );
        setTotalBets(totalBetCount);
        setTotalAmount(totalBetAmount);
        return updated;
      });
    });

    socketRef.current.on("gameUpdated", () => {
      fetchCurrentGame();
      fetchHistory();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [auth]);

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch current game data
  const fetchCurrentGame = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/admin/titli/current-game`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${auth}`,
          },
        },
      );

      if (response?.data.success) {
        setCurrentGame(response.data.game);
        setGameBets(response.data.bets || []);
        setTimmer(response.data.timmer);

        // Calculate totals
        const totalBetCount = response.data.bets?.length || 0;
        const totalBetAmount =
          response.data.bets?.reduce(
            (sum, bet) => sum + parseFloat(bet.betAmount),
            0,
          ) || 0;
        setTotalBets(totalBetCount);
        setTotalAmount(totalBetAmount);
      } else {
        toast.error(response?.data.message || "Failed to fetch game data");
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  useEffect(() => {
    let interval = null;
    if (timmer > 0) {
      interval = setInterval(() => {
        setTimmer((prev) => prev - 1);
      }, 1000);
    } else if (timmer === 0) {
      clearInterval(interval);
      setTimmer(120);
      handleRefresh();
    }
    return () => clearInterval(interval);
  }, [timmer]);

  // Fetch game history
  const fetchHistory = async () => {
    const offset = (currentHistoryPage - 1) * HISTORY_LIMIT;
    try {
      const response = await axios.get(
        `${base_url}/api/admin/titli/game-history?limit=${HISTORY_LIMIT}&offset=${offset}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${auth}`,
          },
        },
      );

      if (response?.data.success) {
        setGameHistory(response.data.history || []);
        setTotalHistoryPages(response.data.totalPages || 1);
      } else {
        toast.error(response?.data.message || "Failed to fetch game history");
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  // Set result
  const handleSetResult = async () => {
    if (!selectedResult) {
      toast.error("Please select a result icon");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${base_url}/api/admin/titli/set-result`,
        {
          game_id: currentGame?.game_id,
          result: `${selectedResult}`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${auth}`,
          },
        },
      );

      if (response?.data.success) {
        toast.success("Result set successfully!");
        fetchCurrentGame();
        fetchHistory();
        setSelectedResult("");
      } else {
        toast.error(response?.data.message || "Failed to set result");
      }
    } catch (error) {
      toast.error("Error setting result");
    } finally {
      setIsProcessing(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchCurrentGame();
    fetchHistory();
  };

  // Handle page change for history
  const handleHistoryPageChange = (newPage) => {
    setCurrentHistoryPage(newPage);
  };

  useEffect(() => {
    fetchCurrentGame();
    fetchHistory();
  }, [currentHistoryPage]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!currentGame) {
    return (
      <div className="wrapper">
        <div
          className={`content-wrapper d-flex flex-column ${
            !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
          }`}
        >
          <div className="page-content">
            <div className="px-3" style={{ overflowX: "hidden" }}>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "80vh" }}
              >
                <div className="text-center">
                  <i className="fa fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                  <h4 className="text-center">Loading Titli Game Data...</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleViewGameDetails = async (gameId) => {
    setIsProcessing(true);
    try {
      const response = await axios.get(
        `${base_url}/api/admin/titli/game-details?game_id=${gameId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${auth}`,
          },
        },
      );

      if (response?.data.success) {
        const gameData = gameHistory.find((g) => g.game_id === gameId);
        setSelectedGame({ ...gameData, bets: response.data.bets || [] });
        setGameBetsDetails(response.data.bets || []);
        setShowDetailsModal(true);
      } else {
        toast.error(response?.data.message || "Failed to fetch game details");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error fetching game details");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="wrapper titli_admin_manage">
        {/* Content Wrapper */}
        <div
          className={`content-wrapper d-flex flex-column ${
            !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
          }`}
          style={{
            marginLeft: !isMobile && isSidebarOpen ? "" : "0",
            transition: "margin-left 0.3s ease",
          }}
        >
          <div className="page-content">
            <div className="px-2 px-md-3" style={{ overflowX: "hidden" }}>
              {/* Header Section */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2">
                <div className="text-center text-md-start">
                  <h2 className="mb-1 h4 h3-md">Manage Titli Game</h2>
                  <p className="text-muted mb-0 small">
                    <span className="d-block d-md-inline">
                      Game ID: <strong>{currentGame.game_id}</strong>
                    </span>
                    <span className="d-block d-md-inline ms-md-2">
                      Status:{" "}
                      <span
                        className={`fw-bold ${
                          currentGame.status === 0
                            ? "text-success"
                            : "text-secondary"
                        }`}
                      >
                        {currentGame.status === 0
                          ? "Active (Bets Open)"
                          : "Closed (Result Set)"}
                      </span>
                    </span>
                  </p>
                </div>
                <div className="d-flex gap-2 w-100 w-md-auto justify-content-center justify-content-md-end">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleRefresh}
                  >
                    <RefreshCw size={14} className="me-1" /> Refresh
                  </button>
                  {currentGame.status === 0 && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleSetResult}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <RefreshCw size={14} className="me-1 fa-spin" />{" "}
                          Processing...
                        </>
                      ) : (
                        <>
                          <Save size={14} className="me-1" /> Set Result
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Stats Cards */}
              <div className="row mb-4 g-3 g-md-4">
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-primary h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-rupee-sign fa-lg fa-2x-md text-primary mb-2"></i>
                      <h4 className="text-primary fw-bold h5 h4-md">
                        ₹{totalAmount.toFixed(2)}
                      </h4>
                      <p className="text-muted mb-0 small">Total Bet Amount</p>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-success h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-ticket fa-lg fa-2x-md text-success mb-2"></i>
                      <h4 className="text-success fw-bold h5 h4-md">
                        {totalBets}
                      </h4>
                      <p className="text-muted mb-0 small">Total Bets Placed</p>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-info h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-clock fa-lg fa-2x-md text-info mb-2"></i>
                      <h4 className="text-info fw-bold h5 h4-md">{timmer}s</h4>
                      <p className="text-muted mb-0 small">Round Timer</p>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-warning h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-gamepad fa-lg fa-2x-md text-warning mb-2"></i>
                      <h4 className="text-warning fw-bold h5 h4-md">
                        {iconData.length}
                      </h4>
                      <p className="text-muted mb-0 small">Icons</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bets Table */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card rounded-3 rounded-md-4 shadow-sm">
                    <div className="card-header bg-light">
                      <h5 className="mb-0">
                        <i className="fa fa-table me-2"></i>Bet Details by Icon
                        & Set Game Result
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        {iconData.map((iconItem) => {
                          const iconBets = gameBets.filter(
                            (bet) => bet.bet === iconItem.name,
                          );

                          const totalIconAmount = iconBets.reduce(
                            (sum, bet) => sum + parseFloat(bet.betAmount || 0),
                            0,
                          );

                          return (
                            <div
                              key={iconItem.name}
                              className="col-6 col-md-4 col-lg-3"
                              onClick={() => setSelectedResult(iconItem.name)}
                              style={{ cursor: "pointer" }}
                            >
                              <div
                                className={`card shadow-sm h-100 ${
                                  selectedResult === iconItem.name
                                    ? "border border-success"
                                    : iconBets.length > 0
                                      ? "border border-primary"
                                      : ""
                                }`}
                              >
                                <div className="card-body d-flex align-items-center justify-content-around">
                                  {/* Left Icon */}
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ fontSize: "32px" }}>
                                      {iconItem.icon}
                                    </span>
                                  </div>

                                  {/* Right Amount */}
                                  <div className="text-end">
                                    <div className="fw-bold text-primary">
                                      ₹{totalIconAmount.toFixed(2)}
                                    </div>
                                    <small className="text-muted">
                                      {iconBets.length} bets
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {selectedResult && (
                          <div className="text-center mb-3">
                            <strong>Selected Result:</strong>{" "}
                            <span className="fs-4 ms-2">
                              {getIconByName(selectedResult)}
                            </span>
                          </div>
                        )}

                        <button
                          className="btn btn-success btn-sm w-25 mx-auto"
                          onClick={handleSetResult}
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            <>
                              <RefreshCw size={14} className="me-1 fa-spin" />{" "}
                              Processing...
                            </>
                          ) : (
                            <>
                              <Save size={14} className="me-1" /> Set Result
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Game History Table */}
              <div className="row">
                <div className="col-12">
                  <div className="card rounded-3 rounded-md-4 shadow-sm">
                    <div className="card-header bg-light">
                      <h5 className="mb-0">
                        <i className="fa fa-history me-2"></i>Recent Game
                        History
                      </h5>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-dark">
                            <tr>
                              <th scope="col" className="col-2 col-md-1">
                                Game ID
                              </th>
                              <th scope="col" className="col-3 col-md-3">
                                Result
                              </th>
                              <th scope="col" className="col-2 col-md-2">
                                Total Bets
                              </th>
                              <th
                                scope="col"
                                className="col-2 col-md-2 text-end"
                              >
                                Total Amount (₹)
                              </th>
                              <th scope="col" className="col-2 col-md-2">
                                Status
                              </th>
                              <th scope="col" className="col-1 col-md-2">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {gameHistory.map((game, idx) => (
                              <tr key={idx}>
                                <td className="col-2 col-md-1">
                                  {game.game_id}
                                </td>
                                <td className="col-3 col-md-3">
                                  {game.result.split(", ").map((item, i) => (
                                    <span key={i} className="me-1 me-md-2 fs-5">
                                      {getIconByName(item.trim())}
                                    </span>
                                  ))}
                                </td>
                                <td className="col-2 col-md-2">
                                  {game.total_bets || 0}
                                </td>
                                <td className="col-2 col-md-2 text-end">
                                  ₹{game.total_amount?.toFixed(2) || 0}
                                </td>
                                <td className="col-2 col-md-2">
                                  <span className="badge bg-success small">
                                    Completed
                                  </span>
                                </td>
                                <td className="col-1 col-md-2 text-center">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() =>
                                      handleViewGameDetails(game.game_id)
                                    }
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {gameHistory.length === 0 && (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-center text-muted py-4"
                                >
                                  No history available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination */}
                      {totalHistoryPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-top">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              handleHistoryPageChange(
                                Math.max(currentHistoryPage - 1, 1),
                              )
                            }
                            disabled={currentHistoryPage === 1}
                          >
                            <ChevronLeft size={16} className="me-1" />
                            Previous
                          </button>
                          <span className="small text-muted">
                            Page {currentHistoryPage} of {totalHistoryPages}
                          </span>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              handleHistoryPageChange(
                                Math.min(
                                  currentHistoryPage + 1,
                                  totalHistoryPages,
                                ),
                              )
                            }
                            disabled={currentHistoryPage === totalHistoryPages}
                          >
                            Next
                            <ChevronRight size={16} className="ms-1" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Game Details Modal */}
              {showDetailsModal && selectedGame && (
                <div
                  className="modal fade show d-block"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          Game Details - ID: {selectedGame.game_id}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowDetailsModal(false)}
                        ></button>
                      </div>
                      <div className="modal-body">
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <strong>Result:</strong>
                            <div>
                              {selectedGame.result
                                .split(", ")
                                .map((item, i) => (
                                  <span key={i} className="me-2 fs-4">
                                    {getIconByName(item.trim())}
                                  </span>
                                ))}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <strong>Total Bets:</strong>{" "}
                            {selectedGame.total_bets || 0}
                          </div>
                          <div className="col-md-3">
                            <strong>Total Amount:</strong> ₹
                            {selectedGame.total_amount?.toFixed(2) || 0}
                          </div>
                          <div className="col-md-3">
                            <strong>Status:</strong>{" "}
                            <span className="badge bg-success">Completed</span>
                          </div>
                        </div>
                        <h6 className="mb-2">Bet Details:</h6>
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="table-dark">
                              <tr>
                                <th>Phone</th>
                                <th>Bet Icon</th>
                                {/* <th>Bet Type</th> */}
                                <th>Amount (₹)</th>
                                <th>Status</th>
                                <th>Win Amount (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {gameBetsDetails.map((bet, idx) => (
                                <tr key={idx}>
                                  <td>{bet.phone}</td>
                                  <td>{getIconByName(bet.bet)}</td>
                                  {/* <td>{bet.bet_type}</td> */}
                                  <td>
                                    ₹{parseFloat(bet.betAmount).toFixed(2)}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${
                                        bet.status === 1
                                          ? "bg-success"
                                          : bet.status === 2
                                            ? "bg-danger"
                                            : "bg-warning"
                                      }`}
                                    >
                                      {bet.status === 1
                                        ? "Win"
                                        : bet.status === 2
                                          ? "Lose"
                                          : "Pending"}
                                    </span>
                                  </td>
                                  <td>
                                    {bet.winAmount
                                      ? `+₹${parseFloat(bet.winAmount).toFixed(
                                          2,
                                        )}`
                                      : "-"}
                                  </td>
                                </tr>
                              ))}
                              {gameBetsDetails.length === 0 && (
                                <tr>
                                  <td
                                    colSpan="6"
                                    className="text-center text-muted py-3"
                                  >
                                    No bets for this game
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowDetailsModal(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 767.98px) {
          .h3-md {
            font-size: 1.75rem !important;
          }
          .fa-2x-md {
            font-size: 1.5rem !important;
          }
          .scrollable-dropdown {
            max-height: 200px;
            overflow-y: auto;
          }
          .text-truncate {
            max-width: 120px;
          }
        }
        @media (min-width: 768px) {
          .h4-md {
            font-size: 1.5rem !important;
          }
          .fa-2x-md {
            font-size: 2rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default AdminManageTitli;
